import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./MoveEvents.css";
import AlertModal from "./AlertModal";
import Common from "../../Common/Common";
import { DateTime, Settings } from "luxon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

Settings.defaultZoneName = "UTC";

// Normalize legacy mode names → canonical modes
function resolveMode(raw) {
  if (raw === "move_all_days") return "shift_from";
  if (raw === "set_day_start") return "move_day";
  return raw || "move_day";
}

function fmtDay(lux) {
  if (!lux?.isValid) return "—";
  return lux.toFormat("ccc, MMM d");
}

function fmtHM(h, m) {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function eventsOnDay(luxDate, scheduleName) {
  return (window.guide_obj?.schedule?.events || [])
    .filter(e => {
      if ((e.Itinerary ?? "Main") !== scheduleName) return false;
      const d = DateTime.fromISO(e.Start, { zone: "utc" });
      return d.year === luxDate.year && d.month === luxDate.month && d.day === luxDate.day;
    })
    .sort((a, b) => new Date(a.Start) - new Date(b.Start));
}

const MODE_CONFIG = {
  // Headings match their menu item's exact label — the dialog title should
  // never say something different from what the user just tapped.
  change_start_date: { heading: "Change Start Date",       icon: solid("calendar-check"),      showFrom: false, showTo: true,  canSubset: false, canTime: false, danger: false },
  move_day:          { heading: "Move days events",         icon: solid("calendar"),            showFrom: true,  showTo: true,  canSubset: true,  canTime: true,  danger: false },
  shift_from:        { heading: "Move all events starting", icon: solid("forward"),             showFrom: true,  showTo: true,  canSubset: false, canTime: false, danger: false },
  delete_day:        { heading: "Delete this one day",       icon: solid("trash"),               showFrom: true,  showTo: false, canSubset: true,  canTime: false, danger: true  },
  // Event-level, not day-level: shifts one specific event and every later
  // event that same day by the same delta — distinct from shift_from
  // (which moves this day and all subsequent days wholesale). Requested
  // 2026-09-17 after "Shift Events" (on an individual event's own menu)
  // turned out to just be shift_from in disguise, losing the event-level
  // granularity its name promised.
  shift_events:      { heading: "Shift Events",        icon: solid("arrows-left-right"),   showFrom: false, showTo: false, canSubset: false, canTime: true,  danger: false },
};

export default function MoveEvents({ show, onHide, mode: modeProp, param_date, ScheduleName, anchorEventId, callback }) {
  const scheduleName = ScheduleName || "Main";
  const mode = resolveMode(modeProp);

  // For set_day_start, param_date has +9h already; pre-open the time panel
  const isSetDayStart = modeProp === "set_day_start";
  const isShiftEvents = mode === "shift_events";

  // The specific event "Shift Events" was invoked on — anchors the shift
  // instead of the day's first event. Falls back to the closest event at
  // or after param_date on the same day if the ID lookup ever misses (e.g.
  // stale ID), so this degrades to "shift from here" rather than silently
  // doing nothing.
  const anchorEvent = useMemo(() => {
    if (!isShiftEvents) return null;
    const events = window.guide_obj?.schedule?.events || [];
    const byId = anchorEventId != null ? events.find(e => String(e.ID) === String(anchorEventId)) : null;
    if (byId) return byId;
    if (!param_date?.isValid) return null;
    const sameDay = events
      .filter(e => (e.Itinerary ?? "Main") === scheduleName)
      .filter(e => { const d = DateTime.fromISO(e.Start, { zone: "utc" }); return d.hasSame(param_date, "day"); })
      .sort((a, b) => new Date(a.Start) - new Date(b.Start));
    return sameDay.find(e => DateTime.fromISO(e.Start, { zone: "utc" }) >= param_date) || sameDay[0] || null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // set_day_start reuses move_day's logic (anchor the day's first event to a
  // chosen time) but is conceptually a single fixed-day operation — there's
  // no "day to move it to" at all, just "what time should this day start."
  // move_day's own From/To date pickers stayed editable under that shared
  // config, so changing either one silently retargeted which day the events
  // landed on instead of just changing the time in place. Hiding both here
  // removes that trap; the day itself is fixed by param_date/initialDate.
  const cfg = isSetDayStart
    ? { ...MODE_CONFIG.move_day, heading: "Set day start time", icon: solid("clock"), showFrom: false, showTo: false }
    : MODE_CONFIG[mode] || MODE_CONFIG.move_day;

  const initialDate = useMemo(() => {
    if (param_date?.isValid) return param_date.startOf("day");
    const iso = window.guide_obj?.schedule?.getFirsEventStartDate(scheduleName);
    return iso
      ? DateTime.fromISO(iso, { zone: "utc" }).startOf("day")
      : DateTime.now().setZone("utc").startOf("day");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [fromDate, setFromDate] = useState(initialDate);
  const [toDate,   setToDate]   = useState(() =>
    mode === "delete_day" || isSetDayStart ? initialDate : initialDate.plus({ days: 1 })
  );

  // Time option — pre-expanded for set_day_start/shift_events, since
  // picking a new time is the entire point of opening either. Defaults to
  // the relevant event's own current start time (not a hardcoded 9:00) —
  // the anchor event for shift_events, the day's first event otherwise.
  const [showTimeOpt, setShowTimeOpt] = useState(isSetDayStart || isShiftEvents);
  const initialTime = useMemo(() => {
    if (isShiftEvents && anchorEvent) {
      const d = DateTime.fromISO(anchorEvent.Start, { zone: "utc" });
      return { hour: d.hour, minute: d.minute };
    }
    if (isSetDayStart && param_date?.isValid) return { hour: param_date.hour, minute: param_date.minute };
    const evs = eventsOnDay(initialDate, scheduleName);
    if (evs.length > 0) {
      const d = DateTime.fromISO(evs[0].Start, { zone: "utc" });
      return { hour: d.hour, minute: d.minute };
    }
    return { hour: 9, minute: 0 };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [startHour,   setStartHour]   = useState(initialTime.hour);
  const [startMinute, setStartMinute] = useState(initialTime.minute);
  // Tracks a manual edit so switching "From" days doesn't clobber a time
  // the user already deliberately chose.
  const [timeTouched, setTimeTouched] = useState(false);

  // Event subset option
  const [showEventOpt, setShowEventOpt] = useState(false);
  const [dayEvents,    setDayEvents]    = useState([]);
  const [selectedIds,  setSelectedIds]  = useState(null); // null = all

  const [alertShow, setAlertShow] = useState(false);
  const [alertMsg] = useState("");

  // Reload events when fromDate changes — also re-anchors the default start
  // time to the new day's first event, unless the user already touched it.
  // Not for shift_events: there's no "From" day to change (showFrom is
  // false) and its anchor is the specific event, not the day's first one.
  useEffect(() => {
    const evs = eventsOnDay(fromDate, scheduleName);
    setDayEvents(evs);
    setSelectedIds(null);
    if (!isShiftEvents && !timeTouched && evs.length > 0) {
      const d = DateTime.fromISO(evs[0].Start, { zone: "utc" });
      setStartHour(d.hour);
      setStartMinute(d.minute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, scheduleName]);

  // ── Preview sentence ──
  const preview = useMemo(() => {
    const fromFmt = fmtDay(fromDate);
    const toFmt   = fmtDay(toDate);
    const total   = dayEvents.length;
    const selCount = selectedIds === null ? total : selectedIds.size;
    const ofStr   = selectedIds !== null ? ` of ${total}` : "";
    const n = `${selCount}${ofStr} event${selCount !== 1 ? "s" : ""}`;

    if (mode === "change_start_date") {
      const oldIso = window.guide_obj?.schedule?.getFirsEventStartDate(scheduleName);
      const oldFmt = oldIso ? fmtDay(DateTime.fromISO(oldIso, { zone: "utc" })) : "?";
      return `Trip currently starts ${oldFmt}. It will be rescheduled to ${toFmt}.`;
    }
    if (mode === "move_day") {
      const timeStr = showTimeOpt ? ` at ${fmtHM(startHour, startMinute)}` : "";
      if (isSetDayStart) return `${n} on ${fromFmt} will start${timeStr}.`;
      return `${n} on ${fromFmt} → ${toFmt}${timeStr}.`;
    }
    if (mode === "shift_from") {
      const days = Math.round(toDate.startOf("day").diff(fromDate.startOf("day"), "days").days);
      const sign = days >= 0 ? "+" : "";
      return `All events from ${fromFmt} onward shift ${sign}${days} day${Math.abs(days) !== 1 ? "s" : ""}.`;
    }
    if (mode === "delete_day") {
      return `${n} on ${fromFmt} will be permanently deleted.`;
    }
    if (mode === "shift_events") {
      if (!anchorEvent) return "Could not find that event.";
      const laterCount = eventsOnDay(DateTime.fromISO(anchorEvent.Start, { zone: "utc" }), scheduleName)
        .filter(e => new Date(e.Start) >= new Date(anchorEvent.Start)).length;
      const name = anchorEvent.Subject || "This event";
      return `"${name}" and ${laterCount - 1} later event${laterCount - 1 !== 1 ? "s" : ""} that day will start at ${fmtHM(startHour, startMinute)}.`;
    }
    return "";
  }, [mode, fromDate, toDate, showTimeOpt, startHour, startMinute, selectedIds, dayEvents, scheduleName, anchorEvent]);

  // ── Execute ──
  const execute = () => {
    const schedule = window.guide_obj?.schedule;
    if (!schedule) return;

    if (mode === "change_start_date") {
      const oldIso   = schedule.getFirsEventStartDate(scheduleName);
      const oldStart = DateTime.fromISO(oldIso, { zone: "utc" });
      schedule.changeStartDate(oldStart, toDate, scheduleName);
      callback?.();
      onHide();
      return;
    }

    if (mode === "move_day") {
      const activeEvents = selectedIds === null
        ? dayEvents
        : dayEvents.filter(e => selectedIds.has(String(e.ID)));

      if (activeEvents.length === 0) { onHide(); return; }

      let timeDiff;
      if (showTimeOpt) {
        // Anchor first active event to the requested start time on the destination day
        const firstStart = DateTime.fromISO(activeEvents[0].Start, { zone: "utc" });
        const toAnchor   = toDate.set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 });
        timeDiff = toAnchor.diff(firstStart);
      } else {
        // Pure day-to-day shift — preserve times of day exactly
        timeDiff = toDate.startOf("day").diff(fromDate.startOf("day"));
      }

      const targetSet = new Set(activeEvents.map(e => String(e.ID)));
      schedule.events.forEach(event => {
        if (!targetSet.has(String(event.ID))) return;
        event.Start = DateTime.fromISO(event.Start, { zone: "utc" }).plus({ seconds: timeDiff / 1000 }).toISO();
        event.End   = DateTime.fromISO(event.End,   { zone: "utc" }).plus({ seconds: timeDiff / 1000 }).toISO();
      });
      callback?.();
      onHide();
      return;
    }

    if (mode === "shift_from") {
      schedule.shiftAllEvents(fromDate.startOf("day"), toDate.startOf("day"), scheduleName);
      callback?.();
      onHide();
      return;
    }

    if (mode === "shift_events") {
      if (!anchorEvent) { onHide(); return; }
      const anchorStart = DateTime.fromISO(anchorEvent.Start, { zone: "utc" });
      const newStart = anchorStart.set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 });
      const timeDiff = newStart.diff(anchorStart);
      if (timeDiff.as("minutes") === 0) { onHide(); return; }
      // Same day, same schedule, at-or-after the anchor's original time —
      // not by ID subset like move_day, since "later events" is exactly
      // the point (there's no fixed list to pick from ahead of time).
      schedule.events.forEach(event => {
        if ((event.Itinerary ?? "Main") !== scheduleName) return;
        const eventStart = DateTime.fromISO(event.Start, { zone: "utc" });
        if (!eventStart.hasSame(anchorStart, "day")) return;
        if (eventStart < anchorStart) return;
        event.Start = eventStart.plus({ seconds: timeDiff.as("seconds") }).toISO();
        event.End   = DateTime.fromISO(event.End, { zone: "utc" }).plus({ seconds: timeDiff.as("seconds") }).toISO();
      });
      callback?.();
      onHide();
      return;
    }

    if (mode === "delete_day") {
      if (selectedIds === null) {
        schedule.deleteDay(fromDate, scheduleName);
      } else {
        schedule.events = schedule.events.filter(e => !selectedIds.has(String(e.ID)));
      }
      callback?.();
      onHide();
      return;
    }
  };

  // ── Event list helpers ──
  const effectiveIds = selectedIds ?? new Set(dayEvents.map(e => String(e.ID)));

  const toggleId = id => {
    setSelectedIds(prev => {
      const base = prev ?? new Set(dayEvents.map(e => String(e.ID)));
      const next = new Set(base);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next.size === dayEvents.length ? null : next;
    });
  };

  const HOURS   = Array.from({ length: 24 }, (_, i) => i);
  const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <div className="modal">
      <Modal
        show={show}
        onHide={onHide}
        size="md"
        dialogClassName="me-modal"
        aria-labelledby="me-modal-title"
        centered
        scrollable={false}
      >
        <Modal.Header>
          <div className="me-header">
            <div className="me-header-left">
              <div className={`me-icon-badge${cfg.danger ? " me-icon-danger" : ""}`}>
                <FontAwesomeIcon icon={cfg.icon} />
              </div>
              <span className="me-title" id="me-modal-title">{cfg.heading}</span>
            </div>
            <div className="me-header-actions">
              <button className="me-btn me-btn-cancel" onClick={onHide}>Cancel</button>
              <button
                className={`me-btn ${cfg.danger ? "me-btn-danger" : "me-btn-save"}`}
                onClick={execute}
              >
                <FontAwesomeIcon icon={cfg.danger ? solid("trash") : solid("check")} />
                {cfg.danger ? "Delete" : "Save"}
              </button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="me-fields">

            {cfg.showFrom && (
              <div className="me-field">
                <span className="me-label">
                  {mode === "delete_day" ? "Day to delete" : mode === "shift_from" ? "Shift from" : "From"}
                </span>
                <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                  selected={Common.JSDateFromLuxonDate(fromDate)}
                  onChange={d => setFromDate(Common.LuxonDateFromJSDate(d).startOf("day"))}
                  dateFormat="MMMM d, yyyy"
                  popperProps={{ strategy: "fixed" }}
                />
              </div>
            )}

            {cfg.showTo && (
              <div className="me-field">
                <span className="me-label">
                  {mode === "change_start_date" ? "New start date" : mode === "shift_from" ? "Shift to" : "To"}
                </span>
                <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                  selected={Common.JSDateFromLuxonDate(toDate)}
                  onChange={d => setToDate(Common.LuxonDateFromJSDate(d).startOf("day"))}
                  dateFormat="MMMM d, yyyy"
                  popperProps={{ strategy: "fixed" }}
                />
              </div>
            )}

            {preview && (
              <p className={`me-preview${cfg.danger ? " me-preview--danger" : ""}`}>{preview}</p>
            )}

            {/* ── Time option ── */}
            {cfg.canTime && (
              <div className="me-expand">
                <button className="me-expand-toggle" onClick={() => setShowTimeOpt(v => !v)}>
                  <FontAwesomeIcon
                    icon={showTimeOpt ? solid("chevron-down") : solid("chevron-right")}
                    className="me-expand-chevron"
                  />
                  {isShiftEvents ? "New start time" : "Start at a different time"}
                  {showTimeOpt && (
                    <span className="me-expand-badge">{fmtHM(startHour, startMinute)}</span>
                  )}
                </button>
                {showTimeOpt && (
                  <div className="me-expand-body">
                    <span className="me-label">{isShiftEvents ? "New start time for this event" : "Start time on destination day"}</span>
                    <div className="me-time-row">
                      <select
                        className="me-time-select"
                        value={startHour}
                        onChange={e => { setStartHour(Number(e.target.value)); setTimeTouched(true); }}
                      >
                        {HOURS.map(h => (
                          <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                        ))}
                      </select>
                      <span className="me-time-colon">:</span>
                      <select
                        className="me-time-select"
                        value={startMinute}
                        onChange={e => { setStartMinute(Number(e.target.value)); setTimeTouched(true); }}
                      >
                        {MINUTES.map(m => (
                          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                        ))}
                      </select>
                    </div>
                    <p className="me-expand-hint">
                      {isShiftEvents
                        ? "This event and every later event that same day shift by the same amount."
                        : "The first event will be anchored to this time; others keep their relative spacing."}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── Event subset option ── */}
            {cfg.canSubset && dayEvents.length > 0 && (
              <div className="me-expand">
                <button className="me-expand-toggle" onClick={() => setShowEventOpt(v => !v)}>
                  <FontAwesomeIcon
                    icon={showEventOpt ? solid("chevron-down") : solid("chevron-right")}
                    className="me-expand-chevron"
                  />
                  {mode === "delete_day" ? "Delete specific events only" : "Move specific events only"}
                  {selectedIds !== null && (
                    <span className="me-expand-badge">{selectedIds.size} / {dayEvents.length}</span>
                  )}
                </button>
                {showEventOpt && (
                  <div className="me-expand-body">
                    {dayEvents.map(ev => {
                      const id      = String(ev.ID);
                      const checked = effectiveIds.has(id);
                      return (
                        <label key={id} className="me-event-row">
                          <input type="checkbox" checked={checked} onChange={() => toggleId(id)} />
                          <span className="me-event-time">{Common.formatTimeLuxon(ev.Start)}</span>
                          <span className="me-event-name">{ev.Subject || "Event"}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        </Modal.Body>
      </Modal>

      {alertShow && (
        <AlertModal
          show={alertShow}
          onHide={() => setAlertShow(false)}
          AlertModalTitle="Error"
          AlertModalMessage={alertMsg}
          AlertModalButtons={[{ Title: "OK", Method: null }]}
        />
      )}
    </div>
  );
}
